import { useState, useEffect, useContext, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { AuthContext } from "@/App";
import { Building2, Calendar, MessageCircle, Plus, TrendingUp, FileText, X, Check, Link2, Unlink } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { VerificationStatus } from "@/components/VerifiedBadge";
import PhoneVerification from "@/components/PhoneVerification";

export default function Dashboard() {
  const { user, refreshUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState({
    listings: [],
    bookings: [],
    services: [],
    franchiseEnquiries: [],
    requirements: []
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [showCreateService, setShowCreateService] = useState(false);
  const [creatingService, setCreatingService] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, email: null });
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    title: "",
    description: "",
    price: "",
    expertise_tags: "",
    duration_minutes: "60"
  });

  // Define callback functions BEFORE useEffects that depend on them
  const fetchCalendarStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/oauth/calendar/status');
      setCalendarStatus(data);
    } catch (error) {
      console.error("Failed to fetch calendar status:", error);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      let listings = [];
      let bookings = [];
      let services = [];
      
      if (user.role === "seller" || user.role === "admin") {
        const listingsRes = await api.get("/my-listings");
        listings = listingsRes.data;
      }
      
      const bookingsRes = await api.get("/bookings");
      bookings = bookingsRes.data;
      
      if (user.role === "consultant") {
        const servicesRes = await api.get(`/consultants/${user.id}/services`);
        services = servicesRes.data;
      }

      // Fetch pending approvals count for admin
      if (user.role === "admin") {
        try {
          const analyticsRes = await api.get("/admin/analytics");
          setPendingCount(analyticsRes.data.pending_listings || 0);
        } catch (error) {
          console.error("Failed to fetch pending count:", error);
        }
      }

      // Fetch franchise enquiries
      try {
        const enquiriesRes = await api.get("/my-franchise-enquiries");
        setStats(prevStats => ({
          ...prevStats,
          franchiseEnquiries: enquiriesRes.data || []
        }));
      } catch (error) {
        console.error("Failed to fetch franchise enquiries:", error);
      }

      // Fetch buyer requirements
      try {
        const requirementsRes = await api.get("/my-requirements");
        setStats(prevStats => ({
          ...prevStats,
          requirements: requirementsRes.data || []
        }));
      } catch (error) {
        console.error("Failed to fetch requirements:", error);
      }

      setStats(prevStats => ({
        ...prevStats,
        listings,
        bookings,
        services
      }));
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // useEffect hooks that use the callbacks
  useEffect(() => {
    fetchDashboardData();
    // Check for calendar connection callback
    if (searchParams.get('calendar_connected') === 'true') {
      toast.success("Google Calendar connected successfully!");
      fetchCalendarStatus();
    } else if (searchParams.get('calendar_error')) {
      toast.error("Failed to connect Google Calendar. Please try again.");
    }
  }, [searchParams, fetchDashboardData, fetchCalendarStatus]);

  useEffect(() => {
    if (user?.role === 'consultant') {
      fetchCalendarStatus();
    }
  }, [user, fetchCalendarStatus]);

  const handleConnectCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const { data } = await api.get('/oauth/calendar/connect');
      // Redirect to Google OAuth
      window.location.href = data.authorization_url;
    } catch (error) {
      toast.error("Failed to initiate Google Calendar connection");
      setConnectingCalendar(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    try {
      await api.delete('/oauth/calendar/disconnect');
      setCalendarStatus({ connected: false, email: null });
      toast.success("Google Calendar disconnected");
    } catch (error) {
      toast.error("Failed to disconnect calendar");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    if (!serviceForm.title || !serviceForm.description || !serviceForm.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreatingService(true);
    try {
      const serviceData = {
        title: serviceForm.title,
        description: serviceForm.description,
        price: parseFloat(serviceForm.price),
        expertise_tags: serviceForm.expertise_tags.split(",").map(tag => tag.trim()).filter(Boolean),
        duration_minutes: parseInt(serviceForm.duration_minutes) || 60
      };

      await api.post("/services", serviceData);
      toast.success("Service created successfully!");
      setShowCreateService(false);
      setServiceForm({
        title: "",
        description: "",
        price: "",
        expertise_tags: "",
        duration_minutes: "60"
      });
      fetchDashboardData(); // Refresh the dashboard
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create service");
    } finally {
      setCreatingService(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-12">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 flex items-center justify-between" data-testid="dashboard-header">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Dashboard</h1>
            <p className="text-slate-600">Welcome back, {user.name}</p>
          </div>
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-sm px-3 py-1">{user.role}</Badge>
        </div>

        {/* Phone Verification Status */}
        <div className="mb-6">
          <VerificationStatus 
            isVerified={user.mobile_verified} 
            onVerify={() => setShowPhoneVerification(true)} 
          />
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {(user.role === "seller" || user.role === "admin") && (
            <Card className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm">
              <CardHeader className="p-0 mb-4">
                <CardTitle className="text-sm font-medium text-slate-500">My Listings</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="flex items-center justify-between">
                  <p className="text-3xl font-bold text-slate-900">{stats.listings.length}</p>
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm">
            <CardHeader className="p-0 mb-4">
              <CardTitle className="text-sm font-medium text-slate-500">
                {user.role === "consultant" ? "Consultations" : "Bookings"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <p className="text-3xl font-bold text-slate-900">{stats.bookings.length}</p>
                <Calendar className="w-8 h-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>

          {user.role === "consultant" && (
            <Card className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm">
              <CardHeader className="p-0 mb-4">
                <CardTitle className="text-sm font-medium text-slate-500">Services</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="flex items-center justify-between">
                  <p className="text-3xl font-bold text-slate-900">{stats.services.length}</p>
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {user.role === "admin" && pendingCount > 0 && (
                <Link to="/admin">
                  <Button className="bg-orange-600 hover:bg-orange-700" data-testid="view-pending-approvals-button">
                    <Building2 className="w-4 h-4 mr-2" />
                    View {pendingCount} Pending Approval{pendingCount > 1 ? 's' : ''}
                  </Button>
                </Link>
              )}
              {(user.role === "seller" || user.role === "admin") && (
                <Link to="/list-business">
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="list-business-button">
                    <Plus className="w-4 h-4 mr-2" />
                    List a Business
                  </Button>
                </Link>
              )}
              <Link to="/messages">
                <Button variant="outline" data-testid="messages-button">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Messages
                </Button>
              </Link>
              {user.role === "buyer" && (
                <Link to="/post-requirement">
                  <Button className="bg-purple-600 hover:bg-purple-700" data-testid="post-requirement-button">
                    <FileText className="w-4 h-4 mr-2" />
                    Post Requirement
                  </Button>
                </Link>
              )}
              {user.role === "consultant" && (
                <Button 
                  variant="outline" 
                  data-testid="create-service-button"
                  onClick={() => setShowCreateService(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Service
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Google Calendar Connection for Consultants */}
        {user.role === "consultant" && (
          <Card className="mb-8 border-2 border-dashed border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Google Calendar Integration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  {calendarStatus.connected ? (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600" />
                        <span className="text-emerald-700 font-medium">Calendar Connected</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        Synced with: <span className="font-medium">{calendarStatus.email}</span>
                      </p>
                      <p className="text-xs text-slate-500">
                        Your availability is synced. Bookings will auto-create Google Meet links.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-slate-700 font-medium">Connect your Google Calendar</p>
                      <p className="text-sm text-slate-500">
                        Enable automatic scheduling and Google Meet links for your consultations
                      </p>
                    </div>
                  )}
                </div>
                {calendarStatus.connected ? (
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={handleDisconnectCalendar}
                    data-testid="disconnect-calendar-btn"
                  >
                    <Unlink className="w-4 h-4 mr-2" />
                    Disconnect
                  </Button>
                ) : (
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={handleConnectCalendar}
                    disabled={connectingCalendar}
                    data-testid="connect-calendar-btn"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    {connectingCalendar ? "Connecting..." : "Connect Google Calendar"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Listings (for sellers) */}
        {(user.role === "seller" || user.role === "admin") && stats.listings.length > 0 && (
          <Card data-testid="my-listings-section">
            <CardHeader>
              <CardTitle>My Listings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.listings.map((listing) => (
                  <div key={listing.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-slate-900">{listing.name}</h3>
                      <p className="text-sm text-slate-600">{listing.industry} • {listing.location}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-slate-900">{formatCurrency(listing.asking_price)}</p>
                      <Badge variant={listing.status === "approved" ? "default" : "secondary"}>
                        {listing.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bookings */}
        {stats.bookings.length > 0 && (
          <Card className="mt-8" data-testid="bookings-section">
            <CardHeader>
              <CardTitle>{user.role === "consultant" ? "Upcoming Consultations" : "My Bookings"}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.bookings.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-slate-900">
                        {user.role === "consultant" ? booking.buyer_name : booking.consultant_name}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {new Date(booking.start_time || booking.booking_date).toLocaleString()}
                      </p>
                      {booking.meeting_link && (
                        <a 
                          href={booking.meeting_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Join Google Meet
                        </a>
                      )}
                    </div>
                    <div className="text-right space-y-1">
                      <Badge variant={booking.status === "confirmed" ? "default" : "secondary"}>
                        {booking.status}
                      </Badge>
                      <Badge variant={booking.payment_status === "paid" ? "default" : "secondary"}>
                        {booking.payment_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Franchise Enquiries */}
        {stats.franchiseEnquiries && stats.franchiseEnquiries.length > 0 && (
          <Card className="mt-8" data-testid="franchise-enquiries-section">
            <CardHeader>
              <CardTitle>My Franchise Enquiries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.franchiseEnquiries.map((enquiry) => (
                  <div key={enquiry.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-purple-50">
                    <div>
                      <h3 className="font-semibold text-slate-900">{enquiry.franchise_brand}</h3>
                      <p className="text-sm text-slate-600">{enquiry.franchise_name}</p>
                      <p className="text-xs text-slate-500 mt-1">Submitted: {new Date(enquiry.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge variant={enquiry.status === "contacted" ? "default" : "secondary"}>
                      {enquiry.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Requirements (Buyer Wishlist) */}
        {stats.requirements && stats.requirements.length > 0 && (
          <Card className="mt-8" data-testid="my-requirements-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>My Business Requirements</CardTitle>
                <Link to="/post-requirement">
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    Post New
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats.requirements.map((req) => (
                  <div key={req.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg bg-blue-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">{req.title}</h3>
                        {req.featured && (
                          <Badge className="bg-amber-500">Featured</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{req.business_type} • {req.industry}</p>
                      <p className="text-sm text-slate-600">Budget: {formatCurrency(req.budget_min)} - {formatCurrency(req.budget_max)}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {req.responses_count} responses • Expires: {new Date(req.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge variant={req.status === "active" ? "default" : "secondary"}>
                        {req.status}
                      </Badge>
                      <Badge variant={req.tier === "free" ? "outline" : "default"} className={req.tier === "concierge" ? "bg-purple-600" : req.tier === "premium" ? "bg-blue-600" : ""}>
                        {req.tier}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phone Verification Modal */}
        <PhoneVerification
          isOpen={showPhoneVerification}
          onClose={() => setShowPhoneVerification(false)}
          onVerified={(phone) => {
            if (refreshUser) refreshUser();
            toast.success("Your account is now verified!");
          }}
          currentMobile={user.mobile}
        />

        {/* Create Service Modal */}
        <Dialog open={showCreateService} onOpenChange={setShowCreateService}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create New Service</DialogTitle>
              <DialogDescription>
                Add a new consulting service to offer to clients.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateService}>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="service-title">Service Title *</Label>
                  <Input
                    id="service-title"
                    placeholder="e.g., Business Valuation Consultation"
                    value={serviceForm.title}
                    onChange={(e) => setServiceForm({ ...serviceForm, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service-description">Description *</Label>
                  <Textarea
                    id="service-description"
                    placeholder="Describe your service in detail..."
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                    rows={3}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="service-price">Price (INR) *</Label>
                    <Input
                      id="service-price"
                      type="number"
                      placeholder="5000"
                      value={serviceForm.price}
                      onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                      min="0"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service-duration">Duration (minutes)</Label>
                    <Input
                      id="service-duration"
                      type="number"
                      placeholder="60"
                      value={serviceForm.duration_minutes}
                      onChange={(e) => setServiceForm({ ...serviceForm, duration_minutes: e.target.value })}
                      min="15"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service-tags">Expertise Tags (comma-separated)</Label>
                  <Input
                    id="service-tags"
                    placeholder="e.g., M&A, Valuation, Due Diligence"
                    value={serviceForm.expertise_tags}
                    onChange={(e) => setServiceForm({ ...serviceForm, expertise_tags: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateService(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creatingService}>
                  {creatingService ? "Creating..." : "Create Service"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}